import { z } from 'zod';

// src/schema.ts
var SPACING_SIDES = ["top", "right", "bottom", "left"];
var DEFAULT_MARGIN = {
  top: { value: 0, unit: "px" },
  right: { value: 0, unit: "auto" },
  bottom: { value: 0, unit: "px" },
  left: { value: 0, unit: "auto" }
};
var DEFAULT_PADDING = {
  top: { value: 0, unit: "px" },
  right: { value: 0, unit: "px" },
  bottom: { value: 0, unit: "px" },
  left: { value: 0, unit: "px" }
};
var DEFAULT_BASE_SPACING = {
  margin: DEFAULT_MARGIN,
  padding: DEFAULT_PADDING,
  width: { value: 0, unit: "auto" },
  maxWidth: { value: 100, unit: "%" }
};
var marginSideSchema = (unit) => z.object({
  value: z.number().default(0).describe("Length for this side"),
  unit: z.enum(["px", "%", "auto"]).default(unit).describe("Unit for this side; auto centers")
}).default({ value: 0, unit });
var paddingSideSchema = () => z.object({
  value: z.number().default(0).describe("Length for this side"),
  unit: z.enum(["px", "%"]).default("px").describe("Unit for this side")
}).default({ value: 0, unit: "px" });
var marginBoxSchema = (defaults) => z.object({
  top: marginSideSchema(defaults.top.unit),
  right: marginSideSchema(defaults.right.unit),
  bottom: marginSideSchema(defaults.bottom.unit),
  left: marginSideSchema(defaults.left.unit)
}).default(defaults).describe("Outer margin");
var paddingBoxSchema = () => z.object({
  top: paddingSideSchema(),
  right: paddingSideSchema(),
  bottom: paddingSideSchema(),
  left: paddingSideSchema()
}).default(DEFAULT_PADDING).describe("Inner padding");
var widthSchema = (defaults) => z.object({
  value: z.number().default(defaults.value).describe("Width length"),
  unit: z.enum(["px", "%", "auto"]).default(defaults.unit).describe("Width unit; auto leaves width unset")
}).default(defaults).describe("Width of the element");
var maxWidthSchema = (defaults) => z.object({
  value: z.number().default(defaults.value).describe("Maximum width length"),
  unit: z.enum(["px", "%", "none"]).default(defaults.unit).describe("Maximum width unit; none removes the constraint")
}).default(defaults).describe("Maximum width of the element");
var baseSpacingSchema = z.object({
  margin: marginBoxSchema(DEFAULT_MARGIN),
  padding: paddingBoxSchema(),
  width: widthSchema(DEFAULT_BASE_SPACING.width),
  maxWidth: maxWidthSchema(DEFAULT_BASE_SPACING.maxWidth)
}).default(DEFAULT_BASE_SPACING).describe("Margin, padding, width and maximum width of the widget");
function coerceSide(raw, extraUnit) {
  if (typeof raw === "number" || typeof raw === "string") {
    if (extraUnit && raw === extraUnit) return { value: 0, unit: extraUnit };
    const n2 = Number(raw);
    return Number.isFinite(n2) ? { value: n2, unit: "px" } : void 0;
  }
  if (!raw || typeof raw !== "object") return void 0;
  const side = raw;
  const n = Number(side.value);
  const unit = side.unit === "%" ? "%" : extraUnit && side.unit === extraUnit ? extraUnit : "px";
  return { value: Number.isFinite(n) ? n : 0, unit };
}
function coerceBox(raw, allowAuto) {
  if (!raw || typeof raw !== "object") return void 0;
  const box = raw;
  const out = {};
  for (const name of SPACING_SIDES) {
    const side = coerceSide(box[name], allowAuto ? "auto" : void 0);
    if (side) out[name] = side;
  }
  return out;
}
function coerceSpacingValue(raw) {
  if (!raw || typeof raw !== "object") return void 0;
  const input = raw;
  const out = { ...input };
  if ("margin" in input) {
    const margin = coerceBox(input.margin, true);
    if (margin) out.margin = margin;
    else delete out.margin;
  }
  if ("padding" in input) {
    const padding = coerceBox(input.padding, false);
    if (padding) out.padding = padding;
    else delete out.padding;
  }
  if ("width" in input) {
    const width = coerceSide(input.width, "auto");
    if (width) out.width = width;
    else delete out.width;
  }
  if ("maxWidth" in input) {
    const maxWidth = coerceSide(input.maxWidth, "none");
    if (maxWidth) out.maxWidth = maxWidth;
    else delete out.maxWidth;
  }
  return out;
}
function liftLegacySpacing(options) {
  if (!options || typeof options !== "object") return options;
  const hasSpacing = "spacing" in options;
  const hasLegacyWidth = "width" in options;
  const hasLegacyMargin = "margin" in options;
  const hasLegacyPadding = "padding" in options;
  if (!hasSpacing && !hasLegacyWidth && !hasLegacyMargin && !hasLegacyPadding) return options;
  const spacing = hasSpacing ? coerceSpacingValue(options.spacing) ?? {} : {};
  if (!("maxWidth" in spacing) && hasLegacyWidth) {
    const legacy = options.width;
    if (legacy && typeof legacy === "object" && "maxWidth" in legacy) {
      const n = Number(legacy.maxWidth);
      if (Number.isFinite(n)) {
        spacing.maxWidth = { value: n, unit: legacy.unit === "px" ? "px" : "%" };
      }
    }
  }
  if (!("margin" in spacing) && hasLegacyMargin) {
    const margin = coerceBox(options.margin, true);
    if (margin) spacing.margin = margin;
  }
  if (!("padding" in spacing) && hasLegacyPadding) {
    const padding = coerceBox(options.padding, false);
    if (padding) spacing.padding = padding;
  }
  return { ...options, spacing };
}

// src/schema.ts
var widgetFilterSchema = z.object({
  // Required filters (at least one must be provided). Descriptions are
  // widget-neutral (#687) — most widgets aren't about images; the gallery
  // widget overrides these with image-specific copy in its own filter schema.
  feed_id: z.number().int().optional().describe("Filter by feed ID"),
  platform: z.string().nullish().describe("Filter by platform UUID"),
  property: z.string().optional().describe("Filter by property slug")
}).describe("Data filtering options for the widget");
var widgetSchema = z.object({
  // Replaces the former `width: { maxWidth, unit }` pair. Defaults reproduce
  // that setting's rendering exactly (`max-width: 100%` + `margin: 0 auto`),
  // and `liftLegacySpacing` migrates saved `width` values on read, so no
  // stored settings need rewriting.
  spacing: baseSpacingSchema,
  colorScheme: z.enum(["light", "dark", "auto", "global"]).default("global").describe(
    "The color scheme of the widget. `global` inherits from the containing layout widget (if any) or the feed's branding."
  ),
  // Deliberately `.optional()`, NOT `.nullish()` (#1168): the Laravel
  // ConvertEmptyStringsToNull artifact only produces null on SCALAR leaves —
  // an object field can never round-trip as ''. Where a widget keeps filter
  // optional, a hypothetical stored `filter: null` is absorbed by
  // `stripNullSettingLeaves` (null → absent → optional). Widgets that
  // OVERRIDE filter as required (most PDP widgets) turn that same null into
  // absent-required — a visible render-boundary error, not a silent blank.
  // `.nullish()` here rippled ZodNullable into four dashboard uiSchema
  // types for no live defect.
  filter: widgetFilterSchema.optional().describe("The filter options for the widget"),
  lightModeColors: z.object({
    accent: z.string().default("#f59e0c").describe("The accent color of the widget")
  }).default({ accent: "#f59e0c" }).describe("Colors in light mode"),
  darkModeColors: z.object({
    accent: z.string().default("#f59e0c").describe("The accent color of the widget")
  }).default({ accent: "#f59e0c" }).describe("Colors in dark mode"),
  font: z.object({
    size: z.number().default(16).describe("The primary font size of the widget"),
    family: z.string().default("Inter").describe("The google font family name of the widget")
  }).default({ size: 16, family: "Inter" }).describe("The base font settings of the widget"),
  language: z.object({
    useAutoDetectedLocale: z.boolean().default(true).describe("Use the auto detected locale of the browser"),
    locale: z.string().default("en_US").describe(
      "The locale of the widget in case of auto detected locale is turned off"
    )
  }).default({ useAutoDetectedLocale: true, locale: "en_US" }).describe("The language settings of the widget"),
  section: z.object({
    show: z.boolean().default(false).describe("Show the section title above the widget content"),
    title: z.string().nullish().default("").describe('Section title text (e.g. "Rooms and Beds")')
  }).default({ show: false, title: "" }).describe("Section heading shown above the widget content"),
  customCss: z.string().nullish().default("").describe("The custom CSS for the widget")
}).describe("The base settings of the widget");
function unwrapSettingSchema(s) {
  let cur = s;
  for (let i = 0; i < 10 && cur; i++) {
    if (cur._def?.innerType) cur = cur._def.innerType;
    else if (cur._def?.schema) cur = cur._def.schema;
    else if (cur._def?.typeName === "ZodLazy") cur = cur._def.getter();
    else if (cur._def?.typeName === "ZodPipeline") cur = cur._def.out;
    else break;
  }
  return cur;
}
function stripNullSettingLeaves(value, schema) {
  const walk = (v, s) => {
    if (v === null || v === "") {
      return s && s.safeParse(v).success ? v : void 0;
    }
    if (Array.isArray(v)) {
      const bare = unwrapSettingSchema(s);
      if (v.length === 0 && (bare?._def?.typeName === "ZodRecord" || bare?._def?.typeName === "ZodObject")) {
        return {};
      }
      if (bare?._def?.typeName === "ZodArray") {
        const elemType = bare._def.type;
        return v.map((el) => walk(el, elemType)).filter((el) => el !== void 0);
      }
      return v;
    }
    if (v && typeof v === "object") {
      const bare = unwrapSettingSchema(s);
      const shape = bare?.shape;
      const isRecord = bare?._def?.typeName === "ZodRecord";
      const recordValueType = isRecord ? bare._def.valueType : void 0;
      const out = {};
      let hadOwnKeys = false;
      for (const [k, val] of Object.entries(v)) {
        if (k === "__proto__" || k === "constructor" || k === "prototype") continue;
        hadOwnKeys = true;
        const next = walk(val, shape?.[k] ?? recordValueType);
        if (next !== void 0) out[k] = next;
      }
      if (isRecord && hadOwnKeys && Object.keys(out).length === 0) return void 0;
      return out;
    }
    return v;
  };
  return walk(value, schema);
}
function coerceScalarBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "1" || s === "true" || s === "yes" || s === "on") return true;
    if (s === "0" || s === "false" || s === "no" || s === "off" || s === "") return false;
  }
  return value;
}
function coerceScalarSettingLeaves(value, schema) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return;
  const bare = unwrapSettingSchema(schema);
  const shape = bare?.shape;
  if (!shape) return;
  const obj = value;
  for (const [key, fieldSchema] of Object.entries(shape)) {
    if (!(key in obj) || obj[key] === void 0 || obj[key] === null) continue;
    const leaf = unwrapSettingSchema(fieldSchema);
    const kind = leaf?._def?.typeName;
    const v = obj[key];
    if (kind === "ZodNumber") {
      if (typeof v === "string" && v.trim() !== "") {
        const num = Number(v);
        if (!Number.isNaN(num)) obj[key] = num;
      }
    } else if (kind === "ZodBoolean") {
      obj[key] = coerceScalarBool(v);
    } else if (kind === "ZodObject") {
      coerceScalarSettingLeaves(v, leaf);
    }
  }
}
function parseWidgetConfig(schema, options, pre = liftLegacySpacing) {
  const stripped = stripNullSettingLeaves(options, schema);
  coerceScalarSettingLeaves(stripped, schema);
  return schema.parse(pre(stripped));
}

export { coerceScalarSettingLeaves, parseWidgetConfig, stripNullSettingLeaves, unwrapSettingSchema, widgetFilterSchema, widgetSchema };
//# sourceMappingURL=schema.js.map
//# sourceMappingURL=schema.js.map